// Generic filter + sort for grid rows. A column opts in via `getValue` (child cols fall back to
// `get`); that value is both the sort key and the filter key.
//
// Filter/sort values MUST be primitives. If `get` returns a render shape (e.g. `{label, color}` for a
// pill), the column must also supply `getValue`/`filterValue` returning a primitive — objects dedupe by
// reference, so each row yields its own duplicate option that matches only itself.
//
// Explicit `filterOptions` must match the data's type exactly (matching is strict `includes`):
// `filterOptions: [{ value: '1' }]` against numeric data silently matches zero rows and reads as an
// empty filter result. Derived options can't hit this — they come from the values they're compared to.

export const columnKey = (column) => column.key || column.header;

export const columnValue = (column, item, row) => {
    if (column.getValue) return column.getValue(item, row);
    if (column.get) return column.get(item, row);
    return undefined;
};

// Value used for filtering (option list + matching); falls back to the sort value.
export const columnFilterValue = (column, item, row) =>
    column.filterValue ? column.filterValue(item, row) : columnValue(column, item, row);

// Numbers numerically, else numeric-aware string compare; nullish last (see applyColumnOps).
const defaultCompare = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
};

// Filter options for a column: explicit `filterOptions` (which alone makes a column filterable — the
// `filterable` flag isn't consulted), else distinct `getValue`s from the data. Nullish and '' are
// skipped, so blank values are unselectable — map them to a sentinel like '—' if they need an option.
const optionsFromPairs = (column, pairs) => {
    if (column.filterOptions) return column.filterOptions;
    if (!column.filterable) return null;
    const values = new Set();
    pairs.forEach(([item, row]) => {
        const value = columnFilterValue(column, item, row);
        if (value != null && value !== '') values.add(value);
    });
    return [...values]
        .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' }))
        .map((value) => ({ value, label: String(value) }));
};

export const resolveFilterOptions = (column, items = [], row) =>
    optionsFromPairs(
        column,
        items.map((item) => [item, row])
    );

// Child columns may derive their value from the parent row, so each child is paired with its own row.
export const resolveChildFilterOptions = (column, rows = [], getChildren) =>
    optionsFromPairs(
        column,
        rows.flatMap((row) => (getChildren(row) || []).map((child) => [child, row]))
    );

// Filter then sort. filters = { [key]: [values] }, sort = { key, dir }. `row` = parent context for child cols.
export const applyColumnOps = (items = [], columns = [], { sort, filters } = {}, row) => {
    let result = items;

    const activeFilters = Object.entries(filters || {}).filter(([, values]) => values && values.length);
    if (activeFilters.length) {
        result = result.filter((item) =>
            activeFilters.every(([key, values]) => {
                const column = columns.find((c) => columnKey(c) === key);
                if (!column) return true;
                return values.includes(columnFilterValue(column, item, row));
            })
        );
    }

    if (sort?.key) {
        const column = columns.find((c) => columnKey(c) === sort.key);
        if (column) {
            const compare = column.sortComparator || defaultCompare;
            result = [...result].sort((a, b) => {
                const av = columnValue(column, a, row);
                const bv = columnValue(column, b, row);
                // Blanks stay at the bottom in both directions — sort() already pins undefined there.
                if (av == null || bv == null) return defaultCompare(av, bv);
                const cmp = compare(av, bv);
                return sort.dir === 'desc' ? -cmp : cmp;
            });
        }
    }

    return result;
};

// Children are already column-filtered. A blank term keeps the row and every child.
// An item match keeps only the matching items and expands the row, even when every item matches.
// An order-only match keeps every child and sets hint.
export const applySearch = ({ term, row, children = [], getOrderText, getChildText }) => {
    const trimmed = String(term ?? '')
        .trim()
        .toLowerCase();
    const list = children || [];
    if (!trimmed) {
        return { include: true, children: list, hint: null, autoExpand: false };
    }

    const childText = getChildText || (() => '');
    const matched = list.filter((child) => {
        const text = childText(child);
        return String(text ?? '')
            .toLowerCase()
            .includes(trimmed);
    });
    if (matched.length > 0) {
        return {
            include: true,
            children: matched,
            hint: null,
            autoExpand: true,
        };
    }

    const orderText = getOrderText ? getOrderText(row) : '';
    if (
        String(orderText ?? '')
            .toLowerCase()
            .includes(trimmed)
    ) {
        return {
            include: true,
            children: list,
            hint: list.length > 0 ? 'order-only' : null,
            autoExpand: false,
        };
    }

    return { include: false, children: [], hint: null, autoExpand: false };
};

// Called only when `autoExpand` changes. A user toggle goes through applyUserToggle
// and is not passed back through here until `autoExpand` changes again.
export const applyAutoExpand = ({ expanded, openedBySearch, autoExpand }) => {
    if (autoExpand) {
        if (!expanded) return { expanded: true, openedBySearch: true };
        return { expanded: true, openedBySearch: !!openedBySearch };
    }
    if (openedBySearch) return { expanded: false, openedBySearch: false };
    return { expanded: !!expanded, openedBySearch: false };
};

export const applyUserToggle = ({ expanded }) => ({
    expanded: !expanded,
    openedBySearch: false,
});
